import { useMemo } from 'react';
import { useTranslate } from 'react-admin';
import { useAppLocale } from '../../../hooks/useAppLocale';
import {
  ListItemButton,
  ListItemAvatar,
  Avatar,
  Badge,
  Box,
  Typography,
  Chip,
  Tooltip,
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import GroupIcon from '@mui/icons-material/Group';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import { green } from '@mui/material/colors';
import { TypingIndicator } from '../../../components/chat/TypingIndicator';
import { ChannelIconBadge } from '../../../components/chat/ChannelIconBadge';
import type {
  Conversation,
  ConversationListItemProps,
} from '../types/tickets.types';
import { TicketStatus, type ChannelType } from '../../../types/chat.types';
import { formatMessagePreview } from '../../../utils/messagePreview';
import { getRelativeTime } from '../../../utils/dateFormat';
import { formatPhoneForDisplay } from '../../../utils/phone';
import { useContactAvatarUrl } from '../../../hooks/useContactAvatarUrl';

/**
 * Ticket Status Badge Component
 * Shows a colored chip with the ticket status
 */
const TicketStatusBadge = ({ status }: { status: TicketStatus }) => {
  const translate = useTranslate();

  const statusConfig: Partial<Record<
    TicketStatus,
    { color: 'error' | 'warning' | 'success'; label: string }
  >> = {
    open: {
      color: 'error',
      label: translate('resources.ticket.status.open'),
    },
    resolved: {
      color: 'warning',
      label: translate('resources.ticket.status.resolved'),
    },
    closed: {
      color: 'success',
      label: translate('resources.ticket.status.closed'),
    },
  };

  const config = statusConfig[status];
  if (!config) return null;

  return (
    <Chip
      icon={<ConfirmationNumberIcon sx={{ fontSize: 14 }} />}
      label={config.label}
      size="small"
      color={config.color}
      variant="outlined"
      sx={{
        height: 20,
        fontSize: 10,
        '& .MuiChip-icon': { fontSize: 12 },
      }}
    />
  );
};

/**
 * Conversation Avatar Component
 * Displays an avatar for the conversation based on its type (contact, group, bot)
 * with a channel icon badge overlay.
 */
const ConversationAvatar = ({
  conversation,
}: {
  conversation: Conversation;
}) => {
  const resolvedAvatarUrl = useContactAvatarUrl(conversation.contact?.avatarFileId);
  const avatarUrl = conversation.contact?.profilePicUrl || resolvedAvatarUrl;

  const channelType = conversation.channelConnection?.channel?.type as ChannelType | undefined;
  const connectionName = conversation.channelConnection?.name;

  const getAvatarContent = () => {
    if (conversation.name) {
      return conversation.name[0].toUpperCase();
    }
    if (conversation.contact?.name) {
      return conversation.contact.name[0].toUpperCase();
    }
    return '?';
  };

  const getAvatarIcon = () => {
    if (conversation.chatbot) {
      return <SmartToyIcon />;
    }
    if (conversation.isGroup) {
      return <GroupIcon />;
    }
    return <ChatIcon />;
  };

  const avatarElement =
    conversation.isGroup || conversation.chatbot ? (
      <Avatar
        sx={{
          bgcolor: conversation.chatbot ? 'secondary.main' : 'primary.main',
          width: 48,
          height: 48,
        }}
      >
        {getAvatarIcon()}
      </Avatar>
    ) : (
      <Avatar
        src={avatarUrl ?? undefined}
        sx={{
          bgcolor: 'primary.light',
          width: 48,
          height: 48,
        }}
      >
        {getAvatarContent()}
      </Avatar>
    );

  return (
    <Box sx={{ position: 'relative' }}>
      {avatarElement}
      <ChannelIconBadge
        channelType={channelType}
        connectionName={connectionName}
        mode="badge"
        size={16}
      />
    </Box>
  );
};

/**
 * ConversationListItem Component
 *
 * Individual conversation item displayed in the conversation list.
 * Shows contact avatar with channel icon badge, conversation subject/name,
 * last message preview, channel connection name, unread badge, ticket status
 * badge, and typing indicator.
 *
 * Features:
 * - Contact avatar with channel icon badge overlay
 * - Unread count badge
 * - Last message preview (truncated)
 * - Relative timestamp (e.g., "2m ago")
 * - Channel connection name display
 * - Ticket status badge for conversations with open tickets
 * - Typing indicator when someone is typing
 * - Visual highlight when selected
 * - Bold text for unread conversations
 */
export function ConversationListItem({
  conversation,
  isSelected,
  onClick,
  unreadCount = 0,
  typingUsers = [],
  showTicketBadge = true,
  className,
}: ConversationListItemProps) {
  const translate = useTranslate();
  const appLocale = useAppLocale();

  // Determine display name: name > contact name > contact email > fallback
  const displayName = useMemo(() => {
    if (conversation.name) {
      return conversation.name;
    }
    if (conversation.contact?.name) {
      return conversation.contact.name;
    }
    if (conversation.contact?.email) {
      return conversation.contact.email;
    }
    return translate('resources.chat.unknownContact');
  }, [conversation, translate]);

  // Smart last message preview (channel-aware, with media type indicators)
  const lastMessagePreview = useMemo(
    () => formatMessagePreview(conversation, { maxLength: 45, translate }),
    [conversation, translate],
  );

  // Relative time display
  const relativeTime = useMemo(
    () => getRelativeTime(conversation.lastActivityAt, translate, appLocale),
    [conversation.lastActivityAt, translate, appLocale]
  );

  // Find open or resolved ticket if exists
  const activeTicket = useMemo(
    () =>
      conversation.tickets?.find(
        (ticket) => ticket.status === TicketStatus.OPEN || ticket.status === TicketStatus.RESOLVED
      ),
    [conversation.tickets]
  );

  // Queue color from active ticket
  const queueColor = activeTicket?.queue?.color || activeTicket?.ticketQueue?.color;
  const queueName = activeTicket?.queue?.name || activeTicket?.ticketQueue?.name;

  // Assigned user
  const assignedUserName = (conversation as any).assignedUser?.name
    || (conversation as any).assignedUser?.email
    || activeTicket?.assignedUser?.name
    || activeTicket?.assignedUser?.email;

  // Channel connection name for display
  const channelConnectionName = conversation.channelConnection?.name;

  // Contact details for tooltip
  const contact = conversation.contact;
  const hasContactDetails = Boolean(displayName || contact?.phoneNumber || contact?.email);
  const phoneFormatted = contact?.phoneNumber ? formatPhoneForDisplay(contact.phoneNumber) : null;

  const handleClick = () => {
    onClick(conversation);
  };

  const isTyping = typingUsers.length > 0;

  return (
    <ListItemButton
      selected={isSelected}
      onClick={handleClick}
      className={className}
      sx={{
        py: 1,
        px: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&.Mui-selected': {
          bgcolor: 'action.selected',
          '&:hover': {
            bgcolor: 'action.selected',
          },
        },
        '&:hover': {
          bgcolor: 'action.hover',
        },
      }}
      aria-label={`${displayName}, ${unreadCount > 0 ? `${unreadCount} unread messages` : 'no unread messages'}`}
    >
      <ListItemAvatar>
        <ConversationAvatar conversation={conversation} />
      </ListItemAvatar>

      <Box sx={{ ml: 1, flex: 1, minWidth: 0 }}>
        {/* Primary: Queue dot + Name + Timestamp */}
        <Box display="flex" alignItems="center" gap={0.5}>
          {/* Queue color dot */}
          {queueColor && (
            <Tooltip title={queueName || ''} arrow placement="top">
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: queueColor,
                  flexShrink: 0,
                }}
              />
            </Tooltip>
          )}

          {/* Contact name with phone/email tooltip */}
          <Tooltip
            title={hasContactDetails ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {/* Contact name with icon */}
                {displayName && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontWeight: 600 }}>
                    <PersonIcon sx={{ fontSize: 14 }} />
                    <span>{displayName}</span>
                  </Box>
                )}
                {/* Phone number */}
                {phoneFormatted && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <PhoneIcon sx={{ fontSize: 14 }} />
                    <Box>
                      <Box component="span">{phoneFormatted.national}</Box>
                      {phoneFormatted.international !== phoneFormatted.national && (
                        <Box component="span" sx={{ ml: 0.75, opacity: 0.7, fontSize: '0.8em' }}>
                          ({phoneFormatted.international})
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}
                {/* Email */}
                {contact?.email && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <EmailIcon sx={{ fontSize: 14 }} />
                    <span>{contact.email}</span>
                  </Box>
                )}
              </Box>
            ) : ''}
            placement="top-start"
            enterDelay={400}
            arrow
            slotProps={{
              tooltip: { sx: { maxWidth: 300, fontSize: 13 } },
            }}
          >
            <Typography
              variant="body2"
              noWrap
              sx={{
                fontWeight: unreadCount > 0 ? 700 : 500,
                flex: 1,
                minWidth: 0,
              }}
            >
              {displayName}
            </Typography>
          </Tooltip>

          {/* Relative timestamp */}
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{ flexShrink: 0, ml: 0.5 }}
          >
            {relativeTime}
          </Typography>
        </Box>

        {/* Secondary: Message preview or typing indicator + unread badge */}
        <Box>
          {isTyping ? (
            <Box sx={{ mt: -0.5, ml: -1 }}>
              <TypingIndicator users={typingUsers} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography
                variant="body2"
                color="textSecondary"
                noWrap
                sx={{
                  fontWeight: unreadCount > 0 ? 600 : 400,
                  flex: 1,
                  minWidth: 0,
                  pr: 1,
                }}
              >
                {lastMessagePreview}
              </Typography>

              {/* Inline unread badge */}
              {unreadCount > 0 && (
                <Badge
                  badgeContent={unreadCount}
                  max={99}
                  sx={{
                    '& .MuiBadge-badge': {
                      position: 'relative',
                      transform: 'none',
                      bgcolor: green[500],
                      color: 'white',
                      fontWeight: 600,
                    },
                    mr: 0.5,
                  }}
                />
              )}
            </Box>
          )}

          {/* Assigned user + channel connection */}
          {(assignedUserName || channelConnectionName) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {assignedUserName && (
                <>
                  <PersonIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    noWrap
                    sx={{ fontSize: 11 }}
                  >
                    {assignedUserName}
                  </Typography>
                </>
              )}
              {assignedUserName && channelConnectionName && (
                <Typography variant="caption" color="textSecondary" sx={{ fontSize: 11 }}>
                  ·
                </Typography>
              )}
              {channelConnectionName && (
                <Typography
                  variant="caption"
                  color="textSecondary"
                  noWrap
                  sx={{ fontSize: 11 }}
                >
                  {channelConnectionName}
                </Typography>
              )}
            </Box>
          )}

          {/* Ticket status badge */}
          {showTicketBadge && activeTicket && (
            <Box mt={0.5}>
              <TicketStatusBadge status={activeTicket.status} />
            </Box>
          )}
        </Box>
      </Box>
    </ListItemButton>
  );
}
